import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import { ApiContext } from "../../../App";
import zod from "zod";
import { convertCsvFileToJson } from "../../../util/csvParser";
import FileInputButton from "../../../components/FileInputButton";
import HvacCsvDataGrid from "./HvacCsvDataGrid";

const ZOD_MISSING_MESSAGE = "Field is required"
const ZOD_INVALID_TYPE_MESSAGE = "Invalid option for field"

const enumErrorFunction = (issue) => issue.input === undefined
    ? ZOD_MISSING_MESSAGE
    : ZOD_INVALID_TYPE_MESSAGE

const hvacFromCsv = zod.object({
    location: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    name: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    tariff: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    set_point: zod.string().optional(),
    on_off: zod.string().optional(),
    mode: zod.string().optional(),
    fan_speed: zod.string().optional(),
    temperature: zod.string().optional(),
    occupancy: zod.string().optional(),
    insulation: zod.string().optional(),
    capacity: zod.string().optional(),
    floor_plan: zod.string().optional(),
    display_on_floor_plan: zod.string().optional(),
    automated: zod.string().optional(),
    average_power_consumption_kw: zod.string().optional(),
    preheat_padding_hours: zod.string().optional()

})

const hvacCreateDto = zod.object({
    facility: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    tariff: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    location: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    name: zod.string().nonempty({ message: ZOD_MISSING_MESSAGE }),
    set_point: zod.string().optional(),
    on_off: zod.string().optional(),
    mode: zod.string().optional(),
    fan_speed: zod.string().optional(),
    temperature: zod.string().optional(),
    occupancy: zod.string().optional(),
    insulation: zod.enum(["LOW", "REGULAR", "HIGH"], {
        error: enumErrorFunction
    }),
    capacity: zod.enum(["LOW", "REGULAR", "HIGH"], {
        error: enumErrorFunction
    }),
    floor_plan: zod.string().optional(),
    display_on_floor_plan: zod.boolean(),
    automated: zod.string().optional(),
    average_power_consumption_kw: zod.string().optional(),
    preheat_padding_hours: zod.string().optional()
})



export const ApiStatusEnum = {
    Success: "Success",
    Error: "Error",
    Invalid: "Invalid"
}

export default function HvacBulkAddPopUp(props) {
    const { open, onCloseWithoutBulkImport, onCloseWithBulkImport, facility, tariffs, points, floorPlans } = props;

    const { t } = useTranslation();

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [hvacs, setHvacs] = React.useState([]);
    const [hvacApiStatusesByRowId, setHvacApiStatusesByRowId] = React.useState({}); // [{ identifier, status: "Success" | "Error | Invalid", message? }]
    const [importInProgress, setImportInProgress] = React.useState(false);
    const [importComplete, setImportComplete] = React.useState(false);
    const [importValid, setImportValid] = React.useState(false);
    const [fileInputDisabled, setFileInputDisabled] = React.useState(false);


    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const { hvacApi } = React.useContext(ApiContext)


    //set_point, on_off, mode, fan_speed, temperature, occupancy 
    const setPoints = React.useMemo(() => points.filter(p => p.key === "SET_POINT"), [points])

    const onOffs = React.useMemo(() => points.filter(p => p.key === "ON_OFF"), [points])

    const modes = React.useMemo(() => points.filter(p => p.key === "MODE"), [points])

    const fanSpeeds = React.useMemo(() => points.filter(p => p.key === "FAN_SPEED"), [points])

    const temperatures = React.useMemo(() => points.filter(p => p.key === "TEMPERATURE"), [points])

    const occupancies = React.useMemo(() => points.filter(p => p.key === "OCCUPIED"), [points])


    const translateSafeParseResult = React.useCallback((safeParseResult) => {
        if (!safeParseResult || safeParseResult.success) {
            return safeParseResult;
        }

        const issues = safeParseResult?.error?.issues ?? [];
        return {
            success: false,
            error: {
                ...safeParseResult.error,
                issues: issues.map((issue) => ({
                    ...issue,
                    message: t(issue.message),
                })),
            },
        };
    }, [t]);

    const parseCsvContent = async (file) => {
        try {
            const data = await convertCsvFileToJson(file);
            const nextHvacs = data.map((row, index) => {
                const csvResultRaw = hvacFromCsv.safeParse(row);
                const csvResult = translateSafeParseResult(csvResultRaw);
                const dtoResult = csvResult.success
                    ? transformCsvPointToPointCreateDto(csvResult.data)
                    : null;


                return {
                    rowId: index,
                    csvResult,
                    dtoResult,
                };
            });
            return nextHvacs;
        } catch (error) {
            console.error("Error parsing CSV file:", error);
            setAlertMessage(t("Error parsing CSV file, please ensure it is properly formatted."));
            setAlertOpen(true);
            return [];
        }
    }

    const areParseResultsAllValid = (hvacs) => {
        return hvacs.every(hvac => hvac.csvResult?.success && hvac.dtoResult?.success);
    }


    const transformCsvPointToPointCreateDto = React.useCallback((hvacFromCsv) => {
        //set_point, on_off, mode, fan_speed, temperature, occupancy all reference points


        let tariff = null;
        if (hvacFromCsv.tariff) {
            tariff = tariffs.find(t => t.name === hvacFromCsv.tariff);
        }

        let floorplan = null;
        if (hvacFromCsv.floor_plan) {
            floorplan = floorPlans.find(fp => fp.name === hvacFromCsv.floor_plan);
        }

        let setpoint = null
        if (hvacFromCsv.set_point) {
            setpoint = setPoints.find(p => p.identifier === hvacFromCsv.set_point);
        }

        let onOff = null
        if (hvacFromCsv.on_off) {
            onOff = onOffs.find(p => p.identifier === hvacFromCsv.on_off);
        }

        let mode = null
        if (hvacFromCsv.mode) {
            mode = modes.find(p => p.identifier === hvacFromCsv.mode);
        }

        let fanSpeed = null
        if (hvacFromCsv.fan_speed) {
            fanSpeed = fanSpeeds.find(p => p.identifier === hvacFromCsv.fan_speed);
        }

        let temperature = null
        if (hvacFromCsv.temperature) {
            temperature = temperatures.find(p => p.identifier === hvacFromCsv.temperature);
        }

        let occupancy = null
        if (hvacFromCsv.occupancy) {
            occupancy = occupancies.find(p => p.identifier === hvacFromCsv.occupancy);
        }

        const displayOnFloorPlanValue = hvacFromCsv.display_on_floor_plan ?? null;
        const displayOnFloorPlanBoolean = displayOnFloorPlanValue === "YES" ? true : displayOnFloorPlanValue === "NO" ? false : undefined;

        const dtoResultRaw = hvacCreateDto.safeParse({
            facility: facility,
            tariff: tariff?.url ?? undefined,
            location: hvacFromCsv.location,
            name: hvacFromCsv.name,
            set_point: setpoint?.url ?? undefined,
            on_off: onOff?.url ?? undefined,
            mode: mode?.url ?? undefined,
            fan_speed: fanSpeed?.url ?? undefined,
            temperature: temperature?.url ?? undefined,
            occupancy: occupancy?.url ?? undefined,
            insulation: hvacFromCsv.insulation,
            capacity: hvacFromCsv.capacity,
            floor_plan: floorplan?.url ?? undefined,
            display_on_floor_plan: displayOnFloorPlanBoolean,
            automated: hvacFromCsv.automated,
            average_power_consumption_kw: hvacFromCsv.average_power_consumption_kw ?? undefined,
            preheat_padding_hours: hvacFromCsv.preheat_padding_hours ?? undefined,
        });
        return translateSafeParseResult(dtoResultRaw);
    }, [tariffs, facility, floorPlans, fanSpeeds, temperatures, occupancies, onOffs, modes, setPoints, translateSafeParseResult]);

    const makeSuccessHandler = (rowId) => {
        return (response) => {
            setHvacApiStatusesByRowId(prev => ({ ...prev, [rowId]: { status: ApiStatusEnum.Success, id: response.id, url: response.url } }));
        }
    }

    const makeErrorHandler = (rowId) => {
        return (response) => {
            const non_field_errors = Object.entries(response)
                .filter(([key]) => key === "non_field_errors")
                .map(([key, value]) => value);
            if (non_field_errors.length > 0) {
                setAlertMessage(non_field_errors.join("; "));
                setAlertOpen(true);
            }
            setHvacApiStatusesByRowId(prev => ({ ...prev, [rowId]: { status: ApiStatusEnum.Error, response } }));
        }
    }

    const submitHvacsToApi = (hvacsToSave) => {
        setImportInProgress(true);
        hvacsToSave.forEach(hvac => {
            if (hvac.dtoResult?.success) {
                const hvacDto = hvac.dtoResult.data;

                const onSuccess = makeSuccessHandler(hvac.rowId);
                const onError = makeErrorHandler(hvac.rowId);

                hvacApi.create(hvacDto, onSuccess, onError);
            } else {
                setHvacApiStatusesByRowId(prev => ({ ...prev, [hvac.rowId]: { status: ApiStatusEnum.Invalid, message: t("Not attempted") } }));
            }
        });
        setImportInProgress(false);
        setImportComplete(true);
    }

    const onFileSelect = async (file) => {
        setHvacs([]);
        setHvacApiStatusesByRowId({});
        setImportValid(false);
        const parsedHvacs = await parseCsvContent(file);
        setHvacs(parsedHvacs);
        setImportValid(areParseResultsAllValid(parsedHvacs));
        setImportComplete(false)
        setImportInProgress(false);
    }

    const handleImport = () => {
        submitHvacsToApi(hvacs);
    }

    const handleClose = () => {

        if (importInProgress) {
            return
        }

        if (importComplete) {
            const successfulHvacApiStatuses = Object.entries(hvacApiStatusesByRowId)
                .filter(([_, status]) => status.status === ApiStatusEnum.Success)

            const ImportedHvacsWithUrls = successfulHvacApiStatuses.map(([rowId, status]) => {
                const hvac = hvacs.find(h => h.rowId === Number(rowId))
                return { ...hvac.dtoResult.data, url: status.url }
            })

            onCloseWithBulkImport(ImportedHvacsWithUrls);
        } else {
            setHvacs([]);
            setHvacApiStatusesByRowId({});
            onCloseWithoutBulkImport();
        }
        setImportComplete(false);
        setFileInputDisabled(false);
    }

    return (
        <React.Fragment>
            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={fullScreen}
                fullWidth
                maxWidth={"md"}
            >
                <DialogTitle>{t("Bulk import HVACs (csv)")}</DialogTitle>
                <DialogContent>
                    <HvacCsvDataGrid points={hvacs} pointApiStatusesByRowId={hvacApiStatusesByRowId} components={{ Toolbar: FileInputButton }} componentsProps={{ toolbar: { onFileSelect: onFileSelect, disabled: fileInputDisabled } }} />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        disabled={importInProgress}
                        autoFocus
                    >
                        {t("Close")}
                    </Button>
                    <Button onClick={handleImport} disabled={importComplete || importInProgress || !importValid}>{t("Import")}</Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
            >
                <DialogTitle id="alert-dialog-title">
                    {t("Problem saving data")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setAlertOpen(false)}
                        autoFocus
                    >
                        {t("OK")}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

